/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery } from '@apollo/client';
import { toFormatCurrency } from '../../../../utils/format';
import DataLoading from '../../../DataLoading';
import moment from 'moment';

const Q_DATASET = gql`
	query get($year: Float!) {
		getMonthlyGrowthReport(year: $year)
	}
`;

export default function MonthlyGrowthSpreadSheetTable({ year = null }) {
	const { t } = useTranslation();
	const toast = useToast();
	const [titles, setTitles] = useState(null);
	const [localYear, setLocalYear] = useState(null);
	const [getDataset, { loading, data, error }] = useLazyQuery(Q_DATASET, { fetchPolicy: 'no-cache' });

	useEffect(() => {
		setLocalYear(year ? parseInt(year) : moment().year());
	}, [year]);

	useEffect(() => {
		if (data?.getMonthlyGrowthReport) {
			setTitles(data?.getMonthlyGrowthReport?.titles);
		}
	}, [data]);

	const load = useCallback(() => {
		getDataset({ variables: { year: localYear } });
	}, [getDataset, localYear]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	return (
		<>
			<Flex justifyContent="flex-start" alignItems="center" ml={4} mr={4}>
				<Text fontSize={'15px'} fontWeight={'bold'}>
					{`${t('1. Monthly Market Growth')} ${t(localYear)}`}
				</Text>
			</Flex>
			<DataLoading loading={loading} loadedData={data?.getMonthlyGrowthReport}>
				{({ loadedData }) => (
					<Box ml={4} mr={4} mt={0} mb={0} overflowX="scroll">
						<table className="spreadsheet_custom_header_report" style={{ marginTop: '10px' }}>
							<thead style={{ height: '35px' }}>
								<tr>
									<th className="sticky-col-1" style={{ color: 'var(--chakra-colors-blue-600)' }}>
										{t('descriptionReport')}
									</th>
									<th className="sticky-col-2" style={{ color: 'var(--chakra-colors-blue-600)' }}>
										{t('Type of Insurance')}
									</th>
									{titles?.map((el) => {
										return <th style={{ color: 'var(--chakra-colors-blue-600)' }}>{t(el?.labelEn)}</th>;
									})}
								</tr>
							</thead>
							<tbody>
								{/* Gross Premium */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" rowSpan={3}>
										{t('Gross Premium')} ($)
									</td>
									<td className="th-label-not-bold sticky-col-2">{t('General Insurance')}</td>
									{loadedData?.grossPremium?.GENERAL_INSURANCE?.map((el, i) => {
										if (i === loadedData?.grossPremium?.GENERAL_INSURANCE?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="th-label-not-bold sticky-col-2">{t('Life Insurance')}</td>
									{loadedData?.grossPremium?.LIFE_INSURANCE?.map((el, i) => {
										if (i === loadedData?.grossPremium?.LIFE_INSURANCE?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="background-light-concrete th-label-total sticky-col-2 sticky-total">{t('Total')}</td>
									{loadedData?.grossPremium?.Total?.map((el, i) => {
										if (i === loadedData?.grossPremium?.Total?.length - 1) {
											return <td className="background-light-concrete th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="background-light-concrete th-value-total">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								{/* Number of Policies */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" rowSpan={3}>
										{t('Number of Policies')} (#)
									</td>
									<td className="th-label-not-bold sticky-col-2">{t('General Insurance')}</td>
									{loadedData?.numPolicies?.GENERAL_INSURANCE?.map((el, i) => {
										if (i === loadedData?.numPolicies?.GENERAL_INSURANCE?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el, null, 0)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el, null, 0)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="th-label-not-bold sticky-col-2">{t('Life Insurance')}</td>
									{loadedData?.numPolicies?.LIFE_INSURANCE?.map((el, i) => {
										if (i === loadedData?.numPolicies?.LIFE_INSURANCE?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el, null, 0)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el, null, 0)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="background-light-concrete th-label-total sticky-col-2 sticky-total">{t('Total')}</td>
									{loadedData?.numPolicies?.Total?.map((el, i) => {
										if (i === loadedData?.numPolicies?.Total?.length - 1) {
											return <td className="background-light-concrete th-value-total">{toFormatCurrency(el, null, 0)}</td>;
										}
										return <td className="background-light-concrete th-value-total">{toFormatCurrency(el, null, 0)}</td>;
									})}
								</tr>
								{/* Sum Insured */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" rowSpan={3}>
										{t('Sum Insured')} ($)
									</td>
									<td className="th-label-not-bold sticky-col-2">{t('General Insurance')}</td>
									{loadedData?.sumInsured?.GENERAL_INSURANCE?.map((el, i) => {
										if (i === loadedData?.sumInsured?.Total?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="th-label-not-bold sticky-col-2">{t('Life Insurance')}</td>
									{loadedData?.sumInsured?.LIFE_INSURANCE?.map((el, i) => {
										if (i === loadedData?.sumInsured?.LIFE_INSURANCE?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="background-light-concrete th-label-total sticky-col-2 sticky-total">{t('Total')}</td>
									{loadedData?.sumInsured?.Total?.map((el, i) => {
										if (i === loadedData?.sumInsured?.Total?.length - 1) {
											return <td className="background-light-concrete th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="background-light-concrete th-value-total">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								{/* Gross Claim incurred */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" rowSpan={3}>
										{t('Gross Claim Incurred')} ($)
									</td>
									<td className="th-label-not-bold sticky-col-2">{t('General Insurance')}</td>
									{loadedData?.claimIncurred?.GENERAL_INSURANCE?.map((el, i) => {
										if (i === loadedData?.claimIncurred?.GENERAL_INSURANCE?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="th-label-not-bold sticky-col-2">{t('Life Insurance')}</td>
									{loadedData?.claimIncurred?.LIFE_INSURANCE?.map((el, i) => {
										if (i === loadedData?.claimIncurred?.LIFE_INSURANCE?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="background-light-concrete th-label-total sticky-col-2 sticky-total">{t('Total')}</td>
									{loadedData?.claimIncurred?.Total?.map((el, i) => {
										if (i === loadedData?.claimIncurred?.Total?.length - 1) {
											return <td className="background-light-concrete th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="background-light-concrete th-value-total">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								{/* Gross Claim Paid */}
								<tr height={'35px'}>
									<td className="th-label sticky-col-1" rowSpan={3}>
										{t('Gross Claim Paid')} ($)
									</td>
									<td className="th-label-not-bold sticky-col-2">{t('General Insurance')}</td>
									{loadedData?.claimPaid?.GENERAL_INSURANCE?.map((el, i) => {
										if (i === loadedData?.claimPaid?.GENERAL_INSURANCE?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="th-label-not-bold sticky-col-2">{t('Life Insurance')}</td>
									{loadedData?.claimPaid?.LIFE_INSURANCE?.map((el, i) => {
										if (i === loadedData?.claimPaid?.LIFE_INSURANCE?.length - 1) {
											return <td className="th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="th-value">{toFormatCurrency(el)}</td>;
									})}
								</tr>
								<tr height={'35px'}>
									<td className="background-light-concrete th-label-total sticky-col-2 sticky-total">{t('Total')}</td>
									{loadedData?.claimPaid?.Total?.map((el, i) => {
										if (i === loadedData?.claimPaid?.Total?.length - 1) {
											return <td className="background-light-concrete th-value-total">{toFormatCurrency(el)}</td>;
										}
										return <td className="background-light-concrete th-value-total">{toFormatCurrency(el)}</td>;
									})}
								</tr>
							</tbody>
						</table>
					</Box>
				)}
			</DataLoading>
		</>
	);
}
